export const EFFECT_INTENSITY_MIN = 0;
export const EFFECT_INTENSITY_MAX = 100;
export const EFFECT_INTENSITY_STEP = 1;
export const DEFAULT_EFFECT_INTENSITY = 80;

export const EFFECT_CHANNELS = ["impact", "particles", "lighting", "motion"] as const;
export type EffectChannel = (typeof EFFECT_CHANNELS)[number];

export interface EffectChannelSetting {
  enabled: boolean;
  intensity: number;
}

export type EffectSettings = Record<EffectChannel, EffectChannelSetting>;

export const DEFAULT_EFFECT_SETTINGS: EffectSettings = {
  impact: { enabled: true, intensity: 48 },
  particles: { enabled: true, intensity: 58 },
  lighting: { enabled: true, intensity: 46 },
  motion: { enabled: true, intensity: 32 },
};

export const EFFECT_CHANNEL_LABELS: Record<EffectChannel, string> = {
  impact: "冲击反馈",
  particles: "粒子碎片",
  lighting: "光影闪光",
  motion: "动作震动",
};

const EFFECT_INTENSITY_STORAGE_KEY = "breacher.effect-intensity";
const EFFECT_SETTINGS_STORAGE_KEY = "breacher.effect-settings";

export function normalizeEffectIntensity(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_EFFECT_INTENSITY;
  return Math.min(EFFECT_INTENSITY_MAX, Math.max(EFFECT_INTENSITY_MIN, Math.round(value)));
}

export function effectIntensityScale(value: number): number {
  return normalizeEffectIntensity(value) / EFFECT_INTENSITY_MAX;
}

function normalizeChannelSetting(
  value: unknown,
  fallback: EffectChannelSetting,
): EffectChannelSetting {
  if (!value || typeof value !== "object") return { ...fallback };
  const candidate = value as Partial<EffectChannelSetting>;
  return {
    enabled: typeof candidate.enabled === "boolean" ? candidate.enabled : fallback.enabled,
    intensity: normalizeEffectIntensity(
      typeof candidate.intensity === "number" ? candidate.intensity : fallback.intensity,
    ),
  };
}

export function normalizeEffectSettings(value: unknown): EffectSettings {
  if (!value || typeof value !== "object") {
    return {
      impact: { ...DEFAULT_EFFECT_SETTINGS.impact },
      particles: { ...DEFAULT_EFFECT_SETTINGS.particles },
      lighting: { ...DEFAULT_EFFECT_SETTINGS.lighting },
      motion: { ...DEFAULT_EFFECT_SETTINGS.motion },
    };
  }
  const candidate = value as Partial<Record<EffectChannel, unknown>>;
  return {
    impact: normalizeChannelSetting(candidate.impact, DEFAULT_EFFECT_SETTINGS.impact),
    particles: normalizeChannelSetting(candidate.particles, DEFAULT_EFFECT_SETTINGS.particles),
    lighting: normalizeChannelSetting(candidate.lighting, DEFAULT_EFFECT_SETTINGS.lighting),
    motion: normalizeChannelSetting(candidate.motion, DEFAULT_EFFECT_SETTINGS.motion),
  };
}

function legacySettings(): EffectSettings {
  let legacy: number | null = null;
  try {
    const stored = localStorage.getItem(EFFECT_INTENSITY_STORAGE_KEY);
    if (stored !== null) legacy = normalizeEffectIntensity(Number(stored));
  } catch {
    return normalizeEffectSettings(undefined);
  }
  if (legacy === null) return normalizeEffectSettings(undefined);
  // Older values also changed element sizes. Migrate them to a moderate feedback budget.
  const intensity = Math.min(60, legacy);
  return {
    impact: { enabled: intensity > 0, intensity: Math.min(55, intensity) },
    particles: { enabled: intensity > 0, intensity: Math.min(65, intensity + 5) },
    lighting: { enabled: intensity > 0, intensity: Math.min(58, intensity) },
    motion: { enabled: intensity > 0, intensity: Math.min(42, Math.round(intensity * 0.7)) },
  };
}

export function readStoredEffectSettings(): EffectSettings {
  try {
    const stored = localStorage.getItem(EFFECT_SETTINGS_STORAGE_KEY);
    return stored === null ? legacySettings() : normalizeEffectSettings(JSON.parse(stored));
  } catch {
    return legacySettings();
  }
}

export function readStoredEffectIntensity(): number {
  const settings = readStoredEffectSettings();
  const values = EFFECT_CHANNELS.map((channel) =>
    settings[channel].enabled ? settings[channel].intensity : 0,
  );
  return Math.round(values.reduce((total, value) => total + value, 0) / values.length);
}

export function applyEffectSettings(input: EffectSettings): void {
  const settings = normalizeEffectSettings(input);
  const impact = settings.impact.enabled ? effectIntensityScale(settings.impact.intensity) : 0;
  const particles = settings.particles.enabled
    ? effectIntensityScale(settings.particles.intensity)
    : 0;
  const lighting = settings.lighting.enabled
    ? effectIntensityScale(settings.lighting.intensity)
    : 0;
  const motion = settings.motion.enabled ? effectIntensityScale(settings.motion.intensity) : 0;
  const root = document.documentElement;
  root.style.setProperty(
    "--effect-intensity",
    String(Math.max(impact, particles, lighting, motion)),
  );
  root.style.setProperty("--effect-impact", String(impact));
  root.style.setProperty("--effect-particles", String(particles));
  root.style.setProperty("--effect-lighting", String(lighting));
  root.style.setProperty("--effect-motion", String(motion));
  root.style.setProperty("--effect-shake-positive", `${(1.2 + 3.2 * impact).toFixed(2)}px`);
  root.style.setProperty("--effect-shake-negative", `${(-1.2 - 3.2 * impact).toFixed(2)}px`);
  root.style.setProperty("--effect-shake-return", `${(-0.6 - 1.2 * impact).toFixed(2)}px`);
  root.style.setProperty("--effect-lunge", `${(1.5 + 4.5 * motion).toFixed(2)}px`);
  root.style.setProperty("--effect-lunge-return", `${(-0.5 - 1.2 * motion).toFixed(2)}px`);
  // Feedback text keeps a stable footprint. Intensity controls its fade and glow instead.
  root.style.setProperty("--effect-float-y", "-16px");
  root.style.setProperty("--effect-pop-y", "4px");
  root.style.setProperty("--effect-hit-brightness", String(1 + 0.65 * lighting));
  root.style.setProperty("--effect-hit-saturation", String(1 - 0.16 * lighting));
  root.style.setProperty("--effect-hit-glow", `${(2 + 6 * lighting).toFixed(2)}px`);
  root.style.setProperty("--effect-damage-font", "1.08rem");
  root.style.setProperty("--effect-room-damage-font", "0.98rem");
  root.dataset.effects = Math.max(impact, particles, lighting, motion) <= 0 ? "off" : "on";
  for (const channel of EFFECT_CHANNELS) {
    root.dataset[`effect${channel[0].toUpperCase()}${channel.slice(1)}`] = settings[channel].enabled
      ? "on"
      : "off";
  }
  try {
    localStorage.setItem(EFFECT_SETTINGS_STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // The selected settings still apply for this page when storage is unavailable.
  }
}

/** Backwards-compatible adapter for old callers and smoke scripts. */
export function applyEffectIntensity(value: number): void {
  const normalized = normalizeEffectIntensity(value);
  applyEffectSettings({
    impact: { enabled: normalized > 0, intensity: Math.min(normalized, 55) },
    particles: { enabled: normalized > 0, intensity: Math.min(normalized, 65) },
    lighting: { enabled: normalized > 0, intensity: Math.min(normalized, 58) },
    motion: { enabled: normalized > 0, intensity: Math.min(normalized, 42) },
  });
}

export function vibrateForEffect(settings: EffectSettings, strength = 1): void {
  if (typeof navigator === "undefined" || typeof navigator.vibrate !== "function") return;
  if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
  const impact = settings.impact.enabled ? effectIntensityScale(settings.impact.intensity) : 0;
  if (impact <= 0) return;
  navigator.vibrate(Math.round((7 + impact * 18) * Math.max(0.35, Math.min(1.4, strength))));
}
